import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthDialog, useCart, useAuth } from '../App';
import { useLogo } from '../hooks/useLogo';
import { Bell, MessageSquare, Package, Tag, Megaphone, Check, Menu, X, Store, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { LANGUAGE_META } from '../i18n/translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getAuthToken() {
  return sessionStorage.getItem('lab_session_token') ||
         sessionStorage.getItem('token') ||
         sessionStorage.getItem('dist_token') || '';
}

function timeAgo(isoStr) {
  if (!isoStr) return '';
  const diff = Math.floor((Date.now() - new Date(isoStr)) / 1000);
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const NOTIF_ICONS = {
  reply:        <MessageSquare className="w-4 h-4 text-blue-500" />,
  enquiry:      <Package className="w-4 h-4 text-green-500" />,
  offer:        <Tag className="w-4 h-4 text-orange-500" />,
  announcement: <Megaphone className="w-4 h-4 text-purple-500" />,
};

function NotificationDropdown({ onClose, onRead }) {
  const navigate  = useNavigate();
  const [notifs, setNotifs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);

  useEffect(() => {
    const load = async () => {
      const token = getAuthToken();
      if (!token) { setLoading(false); return; }
      try {
        const res  = await fetch(`${BACKEND_URL}/api/notifications`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setNotifs(data.notifications || []);
        await fetch(`${BACKEND_URL}/api/notifications/mark-read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({}),
        });
        onRead();
      } catch {}
      setLoading(false);
    };
    load();
  }, [onRead]);

  useEffect(() => {
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [onClose]);

  return (
    <div ref={ref}
      className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 overflow-hidden"
      data-testid="notifications-dropdown">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <span className="text-sm font-bold text-gray-800">Notifications</span>
        {notifs.length > 0 && (
          <span className="text-[10px] text-gray-400 flex items-center gap-1">
            <Check className="w-3 h-3" /> All read
          </span>
        )}
      </div>
      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : notifs.length === 0 ? (
          <div className="text-center py-10 px-4">
            <Bell className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-xs text-gray-400 font-medium">No notifications yet</p>
            <p className="text-[11px] text-gray-300 mt-1">You'll be notified when someone replies to your posts</p>
          </div>
        ) : notifs.map((n) => (
          <button key={n.id}
            onClick={() => { onClose(); if (n.post_id) navigate(`/?post=${n.post_id}`); }}
            className={`w-full text-left px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors flex gap-3 items-start ${!n.is_read ? 'bg-blue-50/40' : ''}`}
            data-testid={`notification-item-${n.id}`}>
            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              {NOTIF_ICONS[n.type] || <Bell className="w-4 h-4 text-gray-400" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-800 leading-snug">{n.title}</p>
              <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>
              <p className="text-[10px] text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
            </div>
            {!n.is_read && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />}
          </button>
        ))}
      </div>
    </div>
  );
}

function LanguageSwitcher() {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGUAGE_META.find(l => l.code === lang) || LANGUAGE_META[0];

  useEffect(() => {
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
        title="Change language"
        data-testid="language-switcher-btn"
      >
        <Globe className="w-4 h-4" />
        <span className="text-xs font-semibold uppercase hidden sm:inline">{lang}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-52 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wide border-b border-gray-100">Language</div>
          <div className="overflow-y-auto max-h-72 py-1">
          {LANGUAGE_META.map(l => (
            <button key={l.code}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${lang === l.code ? 'text-blue-600 font-semibold bg-blue-50' : 'text-gray-700'}`}
              data-testid={`lang-option-${l.code}`}
            >
              <span className="text-base">{l.flag}</span>
              <span>{l.name}</span>
              {lang === l.code && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-500" />}
            </button>
          ))}
          </div>
        </div>
      )}
    </div>
  );
}


function UserMenu({ user, navigate, logout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const rk = user?.user_type || user?.role || '';
  const roleLabel = { researcher: 'Buyer', buyer: 'Buyer', distributor: 'Distributor', distributor_admin: 'Distributor', manufacturer: 'Manufacturer' }[rk];
  const initial = user?.name?.charAt(0).toUpperCase() || user?.company_name?.charAt(0).toUpperCase() || 'U';
  const isSeller = rk === 'distributor' || rk === 'distributor_admin' || rk === 'manufacturer';

  useEffect(() => {
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button onClick={() => setOpen(o => !o)}
        className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 hover:bg-blue-700 transition-colors"
        data-testid="user-menu-btn"
        aria-label="Account menu">
        {initial}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-52 bg-white border border-gray-200 rounded-2xl shadow-lg py-1.5 z-50">
          <div className="px-4 py-2.5 border-b border-gray-100">
            <div className="text-xs font-bold text-gray-900 truncate">{user?.name || user?.company_name || 'Account'}</div>
            <div className="text-xs text-gray-400 truncate">{user?.email || roleLabel || ''}</div>
          </div>
          <button onClick={() => { setOpen(false); navigate('/settings'); }}
            className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            My Account
          </button>
          <div className="my-1 border-t border-gray-100" />
          <button onClick={() => { setOpen(false); logout(); location.reload(); }}
            className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
            <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Navbar({ onSearch, onSubmit, searchPlaceholder }) {
  const navigate = useNavigate();
  const { user, isLoggedIn, logout } = useAuth();
  const { itemCount } = useCart();
  const { openLabAuth } = useAuthDialog();
  const logoUrl = useLogo();

  const loggedIn = !!(isLoggedIn && isLoggedIn());

  // "Register free" always opens auth dialog
  const handleRegisterFree = (closeMobile) => {
    if (closeMobile) closeMobile();
    openLabAuth('', 'register');
  };

  const NAVBAR_EXAMPLES = [
    'Search by product name, Cat. No., or CAS No...',
    'Try: "Cat# 231124" — find by catalogue number',
    'Try: "67-56-1" — search by CAS number',
    'Try: "I\'m looking for ELISA kits for cytokine detection"',
    'Try: "Gibco DMEM without glutamine, 500ml"',
    'Try: "anti-CD3 antibody, flow cytometry grade"',
    'Try: "TaqMan PCR master mix, 96 reactions"',
    'Try: "HiMedia nutrient agar 500g" — brand + product',
  ];

  const [searchVal,   setSearchVal]   = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDrop,    setShowDrop]    = useState(false);
  const [isFocused,   setIsFocused]   = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs,  setShowNotifs]  = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [navPhIdx,    setNavPhIdx]    = useState(0);
  const _preWarmTimer = useRef(null);

  // Pre-warm /api/chat/search cache when dropdown appears so clicking is instant
  useEffect(() => {
    if (_preWarmTimer.current) clearTimeout(_preWarmTimer.current);
    if (!suggestions.length || !searchVal.trim()) return;
    const q = searchVal.trim();
    _preWarmTimer.current = setTimeout(() => {
      fetch(`${BACKEND_URL}/api/chat/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q, session_id: 'prewarm', chat_history: [] }),
      }).catch(() => {});
    }, 400);
    return () => clearTimeout(_preWarmTimer.current);
  }, [suggestions, searchVal]);

  useEffect(() => {
    if (isFocused || searchVal) return;
    const id = setInterval(() => setNavPhIdx(i => (i + 1) % NAVBAR_EXAMPLES.length), 3300);
    return () => clearInterval(id);
  }, [isFocused, searchVal]);

  const debounceRef = useRef(null);
  const wrapperRef  = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    const token = getAuthToken();
    if (!token) { setUnreadCount(0); return; }
    try {
      const res  = await fetch(`${BACKEND_URL}/api/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setUnreadCount(data.count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    if (!loggedIn) { setUnreadCount(0); return; }
    fetchUnreadCount();
    const id = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(id);
  }, [fetchUnreadCount, loggedIn]);

  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDrop(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!searchVal.trim() || searchVal.trim().length < 2) { setSuggestions([]); setShowDrop(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res  = await fetch(`${BACKEND_URL}/api/nav/suggestions?q=${encodeURIComponent(searchVal.trim())}`);
        const data = await res.json();
        const list = data.suggestions || [];
        setSuggestions(list);
        setShowDrop(list.length > 0);
      } catch { setSuggestions([]); }
      finally { setLoading(false); }
    }, 300);
  }, [searchVal]);

  useEffect(() => {
    if (!onSearch) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { onSearch(searchVal.trim()); }, 350);
  }, [searchVal, onSearch]);

  const goSearch = (q) => {
    if (onSubmit) { onSubmit(q); setSearchVal(''); return; }
    if (onSearch) { onSearch(q); return; }
    sessionStorage.setItem('last_search', q);
    window.dispatchEvent(new CustomEvent('biomall:search-updated', { detail: { q } }));
    navigate(`/?q=${encodeURIComponent(q)}`);
    setSearchVal('');
    setShowDrop(false);
  };

  // Used when clicking a specific suggestion — pins the brand so it appears first in results
  const goSearchWithBrand = (q, brand) => {
    if (onSubmit) { onSubmit(q); setSearchVal(''); return; }
    if (onSearch) { onSearch(q); return; }
    sessionStorage.setItem('last_search', q);
    window.dispatchEvent(new CustomEvent('biomall:search-updated', { detail: { q } }));
    const url = brand
      ? `/?q=${encodeURIComponent(q)}&brand_pin=${encodeURIComponent(brand)}`
      : `/?q=${encodeURIComponent(q)}`;
    navigate(url);
    setSearchVal('');
    setShowDrop(false);
  };

  const handleBellClick = () => {
    setShowNotifs(v => !v);
    if (!showNotifs) setUnreadCount(0);
  };
  const handleRead  = useCallback(() => setUnreadCount(0), []);
  const handleClose = useCallback(() => setShowNotifs(false), []);

  return (
    <>
    <div className="h-[108px] sm:h-14" aria-hidden="true" />
    <header className="bg-white border-b border-gray-200 fixed top-0 left-0 right-0 w-full z-50">
      <div className="max-w-6xl mx-auto px-4 h-14 flex items-center gap-2 sm:gap-4">

        {/* Logo */}
        <div className="flex items-center flex-shrink-0 cursor-pointer" onClick={() => navigate('/')}>
          {logoUrl
            ? <img src={logoUrl} alt="BioMall" className="h-7 w-auto object-contain max-w-[110px] sm:max-w-none" />
            : <span className="text-lg font-bold text-gray-900">bio<span className="text-blue-600">mall</span>.ai</span>
          }
        </div>

        {/* Search — hidden on mobile (hero has its own search) */}
        <div className="hidden sm:flex flex-[2] min-w-0 max-w-2xl relative" ref={wrapperRef}>
          <div className="relative w-full">
            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-600 z-10" viewBox="0 0 16 16" fill="none">
              <path d="M8 1.5L9.2 6H13.5L10.1 8.8L11.3 13.5L8 10.8L4.7 13.5L5.9 8.8L2.5 6H6.8L8 1.5Z" fill="currentColor"/>
            </svg>
            <input
              value={searchVal}
              onChange={e => setSearchVal(e.target.value)}
              onClick={() => { setIsFocused(true); }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => { setIsFocused(false); setTimeout(() => setShowDrop(false), 150); }}
              onKeyDown={e => {
                if (e.key === 'Enter' && searchVal.trim()) goSearch(searchVal.trim());
                if (e.key === 'Escape') { setShowDrop(false); setIsFocused(false); e.target.blur(); if (onSearch) { setSearchVal(''); onSearch(''); } }
              }}
              placeholder={NAVBAR_EXAMPLES[navPhIdx]}
              className="w-full pl-10 pr-3 py-2.5 text-sm border-2 border-blue-300 rounded-full outline-none focus:border-blue-400 focus:shadow-lg shadow-sm transition-all placeholder-gray-400 text-gray-800 font-medium"
            />
            {loading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>

          {(showDrop && isFocused && suggestions.length > 0) && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden" style={{width: '340px'}}>
              <div className="max-h-[420px] overflow-y-auto">
              {suggestions.map((s, i) => (
                <div key={i} onMouseDown={() => goSearchWithBrand(s.product_name || s, typeof s === 'object' ? s.brand_name : '')}                  className="flex items-start gap-2 px-4 py-2.5 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0">
                  {s.is_partner ? (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-blue-400 flex-shrink-0 mt-0.5">
                      <path d="M7 1L8.1 5H12L8.9 7.5L10 11.5L7 9L4 11.5L5.1 7.5L2 5H5.9L7 1Z" fill="currentColor"/>
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-gray-300 flex-shrink-0 mt-0.5">
                      <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M9.5 9.5L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  )}
                  <div className="min-w-0">
                    <span className="text-sm text-gray-700 truncate block">{s.product_name || s}</span>
                    {(s.brand_name || s.catalog_number) && (
                      <span className="text-xs text-gray-400 truncate block mt-0.5">
                        {s.brand_name}{s.brand_name && s.catalog_number ? ' · ' : ''}{s.catalog_number}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              </div>
              <div onMouseDown={() => goSearch(searchVal.trim())}
                className="px-4 py-2.5 text-sm text-blue-600 font-semibold hover:bg-gray-50 cursor-pointer border-t border-gray-100">
                Search for "{searchVal.trim()}" →
              </div>
            </div>
          )}
        </div>

        {/* Nav links — desktop only */}
        <nav className="hidden md:flex items-center gap-1 ml-auto">
          <button onClick={() => navigate('/brands', { state: { clearSearch: Date.now() } })}
            className="px-3 py-2 text-sm font-medium text-gray-500 rounded-lg hover:text-gray-800 hover:bg-gray-50 transition-colors flex items-center gap-1">
            <Store className="w-3.5 h-3.5 text-blue-500" />
            Brand Store
          </button>
          <button onClick={() => navigate('/categories')}
            className="px-3 py-2 text-sm font-medium text-gray-500 rounded-lg hover:text-gray-800 hover:bg-gray-50 transition-colors">
            Categories
          </button>
        </nav>

        {/* Language switcher */}
        <LanguageSwitcher />

        {/* Auth section */}
        {loggedIn ? (
          <UserMenu user={user} navigate={navigate} logout={logout} />
        ) : (
          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
            <button onClick={() => openLabAuth('', 'login')}
              className="px-4 py-1.5 text-xs font-semibold text-blue-600 border border-blue-200 rounded-full hover:bg-blue-50 transition-colors whitespace-nowrap"
              data-testid="navbar-signin-btn">
              Sign in
            </button>
            <button onClick={() => handleRegisterFree(null)}
              className="px-4 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-full transition-colors whitespace-nowrap"
              data-testid="navbar-register-btn">
              Register free
            </button>
          </div>
        )}

        {/* Notification Bell (logged-in only) */}
        {loggedIn && (
          <div className="relative flex-shrink-0">
            <button onClick={handleBellClick} data-testid="notification-bell"
              className="relative p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700">
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span data-testid="notification-badge"
                  className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            {showNotifs && <NotificationDropdown onClose={handleClose} onRead={handleRead} />}
          </div>
        )}

        {itemCount > 0 && (
          <button onClick={() => navigate('/')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-xs font-semibold text-blue-700 hover:bg-blue-100 transition-colors flex-shrink-0">
            {itemCount} in cart
          </button>
        )}

        {/* Hamburger button — mobile only */}
        <button
          onClick={() => setMobileOpen(o => !o)}
          className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
          aria-label="Toggle menu"
        >
          {mobileOpen
            ? <X className="w-5 h-5 text-gray-600" />
            : <Menu className="w-5 h-5 text-gray-600" />
          }
        </button>
      </div>

      {/* Mobile dropdown menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white px-4 py-3 flex flex-col gap-1 shadow-sm">
          <button onClick={() => { navigate('/brands', { state: { clearSearch: Date.now() } }); setMobileOpen(false); }}
            className="text-left px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors flex items-center gap-1.5">
            <Store className="w-4 h-4 text-blue-500" />
            Brand Store
          </button>
          <button onClick={() => { navigate('/categories'); setMobileOpen(false); }}
            className="text-left px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors">
            Categories
          </button>
          {!loggedIn && (
            <>
              <div className="my-1 border-t border-gray-100" />
              <button onClick={() => { openLabAuth('', 'login'); setMobileOpen(false); }}
                className="px-4 py-2.5 text-sm font-semibold text-blue-600 border border-blue-200 rounded-xl hover:bg-blue-50 transition-colors text-left"
                data-testid="mobile-signin-btn">
                Sign in
              </button>
              <button onClick={() => handleRegisterFree(() => setMobileOpen(false))}
                className="px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors text-left"
                data-testid="mobile-register-btn">
                Register free
              </button>
            </>
          )}
        </div>
      )}
      {/* Mobile search row — always shown, sticky with the fixed header */}
      <div className="sm:hidden bg-white border-t border-gray-100 px-3 py-2">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            value={searchVal}
            onChange={e => { setSearchVal(e.target.value); if (onSearch) onSearch(e.target.value); }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => { setIsFocused(false); setTimeout(() => setShowDrop(false), 150); }}
            onKeyDown={e => {
              if (e.key === 'Enter' && searchVal.trim()) {
                setShowDrop(false);
                if (onSubmit) { onSubmit(searchVal.trim()); setSearchVal(''); }
                else if (onSearch) { onSearch(searchVal.trim()); }
                else { sessionStorage.setItem('last_search', searchVal.trim()); navigate(`/?q=${encodeURIComponent(searchVal.trim())}`); setSearchVal(''); }
              }
              if (e.key === 'Escape') setShowDrop(false);
            }}
            placeholder={NAVBAR_EXAMPLES[navPhIdx]}
            className="w-full pl-9 pr-9 py-2.5 text-sm border-2 border-gray-200 rounded-xl bg-gray-50 outline-none focus:border-blue-400 focus:bg-white transition-colors"
            data-testid="navbar-mobile-search-input"
          />
          {searchVal && (
            <button
              onClick={() => { setSearchVal(''); setSuggestions([]); setShowDrop(false); if (onSearch) onSearch(''); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          {/* Mobile AI suggestions dropdown */}
          {showDrop && isFocused && suggestions.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-[200] overflow-hidden">
              <div className="max-h-[420px] overflow-y-auto">
              {suggestions.map((s, i) => (
                <div key={i} onMouseDown={() => {
                  setShowDrop(false);
                  setSearchVal('');
                  const q = s.product_name || s;
                  const brand = typeof s === 'object' ? s.brand_name : '';
                  if (onSubmit) onSubmit(q);
                  else {
                    sessionStorage.setItem('last_search', q);
                    const url = brand ? `/?q=${encodeURIComponent(q)}&brand_pin=${encodeURIComponent(brand)}` : `/?q=${encodeURIComponent(q)}`;
                    navigate(url);
                  }
                }}
                  className="flex items-start gap-2.5 px-4 py-2.5 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0">
                  {s.is_partner ? (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-blue-400 flex-shrink-0 mt-0.5">
                      <path d="M7 1L8.1 5H12L8.9 7.5L10 11.5L7 9L4 11.5L5.1 7.5L2 5H5.9L7 1Z" fill="currentColor"/>
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-gray-300 flex-shrink-0 mt-0.5">
                      <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M9.5 9.5L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  )}
                  <div className="min-w-0">
                    <span className="text-sm text-gray-700 truncate block">{s.product_name || s}</span>
                    {(s.brand_name || s.catalog_number) && (
                      <span className="text-xs text-gray-400 truncate block mt-0.5">
                        {s.brand_name}{s.brand_name && s.catalog_number ? ' · ' : ''}{s.catalog_number}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
    </>
  );
}
